// v0.18.92 — 修「移动端『转学员办卡』弹层在 iOS 上划不动」(辉哥 11:19 真机复测)
//
// ─────────────────────────────────────────────────────────────────────────────
// ## 根因 (已**实测取证**, 不是推理假设)
// ─────────────────────────────────────────────────────────────────────────────
//   Flarum core 的 `ModalManager` 用 **`body-scroll-lock`** 锁 body 滚动:
//     vendor/flarum/core/js/src/common/components/ModalManager.tsx
//       → disableBodyScroll(this.activeDialogManagerElement, { reserveScrollBarGap })
//
//   该库 v4.0.0-beta.0 在 **iOS 分支** 会做两件事:
//     ① 在 **`.ModalManager`** 上挂 `targetElement.ontouchmove`,
//        内部用 **`.ModalManager` 自己的 scrollTop / scrollHeight** 判边界:
//          - `scrollTop === 0 && clientY > 0`  → preventDefault (在顶部还往下拉)
//          - `滚到底 && clientY < 0`            → preventDefault (在底部还往上推)
//     ② 再在 **document** 上挂一个 touchmove → preventDefault 兜底
//        (Flarum **没传** `allowTouchMove` ⇒ **每个** touchmove 都被 cancel)
//
//   而「转学员办卡」这个弹层的**真滚动容器是嵌套的 `.Modal-body`**,
//   `.Modal-content` 又被 core 的移动端规则 `min-height: 100dvh` 顶成**满屏**
//   ⇒ `.ModalManager` 的 scrollHeight === clientHeight === 视口高、scrollTop 恒为 0
//   ⇒ **上面两个分支双向全中** ⇒ 手指滑动被完全 cancel ⇒ **划不动**。
//
//   ⚠️ **为什么只有「转学员办卡」**: 别的 modal 要么由 `.ModalManager` 自己滚
//      (scrollHeight > clientHeight ⇒ 库的边界判断成立 → 正常),
//      要么内容不满屏 (无需滚 → 察觉不到)。
//      **只有「转学员办卡」是「满屏 `.Modal-content` + 内嵌滚动容器 `.Modal-body`」这个组合**。
//
// ─────────────────────────────────────────────────────────────────────────────
// ## 修法: 在**我们自己**的 `.Modal-content` 上装一个 **iOS only** 的 touchmove 监听,
//          回调里**只** `e.stopPropagation()`
// ─────────────────────────────────────────────────────────────────────────────
//   · `stopPropagation` 只停**传播**, **不改默认行为**
//     ⇒ 浏览器的**原生滚动照常发生** ⇒ `.Modal-body` 能滚。
//   · `.Modal-content` 是 `.ModalManager` 的**后代**、也是 document 的**后代**
//     ⇒ 事件在到达 `.ModalManager.ontouchmove` 和 document 级监听**之前**就被拦下
//     ⇒ body-scroll-lock 那两个 preventDefault 分支**根本不会执行**。
//   ⛔ **绝不能 `preventDefault()`**: 那会直接取消原生滚动 ⇒ 又变回划不动。
//   ⛔ **不能用 capture 阶段**: capture 在祖先上拦会**掐断事件到 target 的投递**
//     ⇒ 输入框自己的 touch 行为会受影响。
//   ✅ 用 **bubble 阶段**挂在 `.Modal-content`: 事件已到 target (输入框正常收到),
//     只是不再往上冒到 `.ModalManager` / document。
//   ⛔ **不要改成「给 `.Modal-body` 加监听」**: 结果面板分支用的是 `key="rbody"` 的
//     **另一个** `.Modal-body` 元素 (Mithril 换 key ⇒ 换元素 ⇒ 监听会丢);
//     `.Modal-content` 元素在两个分支间**不变**。
//
//   ⚠️ **body 滚动锁不会因此失效**: body-scroll-lock 在 iOS 分支里是把
//      `document.body` 设成 `position: fixed` + `top: -scrollY` 来锁 body 的
//      (实测 `public/assets/forum.js` 的 `disableBodyScroll` iOS 分支),
//      **不是**靠那个 document 级 touchmove 兜底 ⇒ 我们截断传播后,
//      弹层后面的页面照样滚不动。
//
// ─────────────────────────────────────────────────────────────────────────────
// ## 为什么 iOS 判定必须与 `body-scroll-lock` **逐字一致**
// ─────────────────────────────────────────────────────────────────────────────
//   那个库在 **模块加载时**算一次 `isIosDevice`:
//     window.navigator && window.navigator.platform && (
//       /iP(ad|hone|od)/.test(window.navigator.platform) ||
//       window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1
//     )
//   ⚠️ **不要**用 `'ontouchstart' in window` / `navigator.maxTouchPoints > 0`
//      之类**更宽**的判定 —— 那会让 **Android / 桌面触屏**也进入我们这段逻辑
//      (那里 body-scroll-lock 走的是**非 iOS 分支**, 根本不挂 touchmove
//       ⇒ 我们这段纯属多余副作用)。
//   ⇒ 本文件在**模块加载时**算一次, 与库同一时刻、同一判据。

/**
 * iOS 判定 —— 与 `body-scroll-lock` 的 `isIosDevice` **逐字一致**
 *
 * ⚠️ 判定**必须在模块加载时**做 (跟库一样): 库是在模块加载时算一次的,
 *    后面 `navigator.platform` 再变也不会重算 ⇒ 我们必须跟它**同一时刻**取同一个值,
 *    否则会出现「库走了非 iOS 分支 (没挂 touchmove) / 我们却挂了监听」的错配。
 */
const IS_IOS_DEVICE = (() => {
  try {
    return !!(typeof window !== 'undefined' && window.navigator && window.navigator.platform && (/iP(ad|hone|od)/.test(window.navigator.platform) || window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1));
  } catch (e) {
    return false;
  }
})();

/** 已经装过监听的元素 (⛔ 用 WeakSet: 不往 DOM 上挂标记位, 元素回收时自动消失) */
const installed = new WeakSet();

/**
 * 本机是不是 iOS (导出出来给调用方 / 将来别的 modal 复用)
 *
 * @returns {boolean}
 */
export function isIosDevice() {
  return IS_IOS_DEVICE;
}

/**
 * 给弹层的 `.Modal-content` 装 iOS 触摸滚动修复 (v0.18.92)
 *
 * ## 干什么
 *   在 `.Modal-content` 上装一个 **bubble 阶段**的 `touchmove` 监听,
 *   回调里**只** `e.stopPropagation()` ⇒ 事件不再冒到 `.ModalManager` / document
 *   ⇒ `body-scroll-lock` 的两个 preventDefault 分支**不会执行**
 *   ⇒ 浏览器原生滚动照常发生 (`.Modal-body` 能上下滑)。
 *
 * ## 为什么不碰样式
 *   ⛔ 本批**只改 JS**: `.Modal-body` 仍是**唯一**滚动容器、
 *      `.Modal-footer` 仍**常驻底部不随内容滚走** (v0.18.85 定的版式, 不许推翻)。
 *   ⛔ 本函数**不改任何样式**, 也不调 `preventDefault`。
 *
 * ## 幂等
 *   同一个元素重复调用**不叠加**监听 (WeakSet 记账) ⇒
 *   `oncreate` + `onupdate` 都调它也只会有一个监听。
 *
 * ## 非 iOS
 *   直接 return —— **一个监听都不留** ⇒ Android / 桌面端行为**零变化**。
 *
 * @param {Element|null|undefined} modalContentEl 弹层的 `.Modal-content` 元素
 * @returns {boolean} true = 已装 (或本来就有); false = 没装 (非 iOS / 元素不存在)
 */
export default function installModalTouchScroll(modalContentEl) {
  // ⛔ 找不到元素**不许抛** —— 抛异常会把整个 modal 弄挂 (静默 no-op)
  if (!modalContentEl || typeof modalContentEl.addEventListener !== 'function') {
    return false;
  }
  // ⛔ 非 iOS 直接返回 —— 不许在非 iOS 上留任何监听
  if (!IS_IOS_DEVICE) {
    return false;
  }
  // 幂等: 同一个元素不许叠加监听
  if (installed.has(modalContentEl)) {
    return true;
  }
  installed.add(modalContentEl);
  modalContentEl.addEventListener('touchmove', event => {
    // ⚠️ **只** stopPropagation —— ⛔ 不许 preventDefault (那会取消原生滚动),
    //    ⛔ 不许改任何样式。
    //    `passive: true` 只影响 preventDefault 是否被允许, stopPropagation 照常生效;
    //    用 passive 还多一层保险: 万一以后谁手滑写了 preventDefault, 浏览器会忽略它。
    event.stopPropagation();
  }, {
    passive: true
  });
  return true;
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/ModalTouchScroll', installModalTouchScroll);